import React, { useState, useEffect } from "react";
import Progress from "./Progress";
import { mergeStyleSets, Pivot, PivotItem, getTheme, MessageBar, MessageBarType } from "@fluentui/react";
import Header from "./Header";
import MainTab from "./MainTab";
import TimerHOC from "./TimerHOC";
import useCaseSummary from "../hooks/queries/useCaseSummary";
import SnippetsTabComponent from "./SnippetsTabComponent";
import env from "../../config.json";

const pivotStyles = {
  root: { button: { width: "50%" } },
  link: {
    selectors: {
      ":hover": {
        background: "#7a5cf0",
      },
    },
  },
  linkIsSelected: [
    {
      backgroundColor: "#7a5cf0 !important",
    },
  ],
};

const snippetOnlyPivotStyles = { ...pivotStyles, root: { button: { width: "100%" } } };

const getClassNames = (theme) =>
  mergeStyleSets({
    root: {
      display: "flex",
      flexDirection: "column",
      background: theme.palette.themeBackground,
      minHeight: "100vh",
      borderLeft: `1px solid ${theme.palette.borderDark}`,
    },
    main: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      flexGrow: 1,
    },
  });

const MAIN_TAB = "Timer";
const defaultMessageBar = { isOpen: false, message: "", type: MessageBarType.success };

const App = ({ isOfficeInitialized }) => {
  let theme = getTheme();
  let { root, main } = getClassNames(theme);
  const [currentTab, setCurrentTab] = useState(MAIN_TAB);
  const [selectedKey, setSelectedKey] = useState(0);
  const [customProps, setCustomProps] = useState({});
  const [messageBar, setMessageBar] = useState(defaultMessageBar);

  const { data: caseSummary } = useCaseSummary(customProps?.CaseId, env.environment);
  const hasOpenedFromCaseNest = !!customProps?.CaseId;

  useEffect(() => {
    const setProps = async () => {
      await Word.run(async (context) => {
        var properties = context.document.properties;
        properties.load("*");

        var customProperties = properties.customProperties;
        customProperties.load("key,type,value");
        await context.sync();

        let obj = {};
        for (let i = 0; i < customProperties.items.length; i++) {
          obj = { ...obj, [customProperties.items[i].key]: customProperties.items[i].value };
        }
        if (env.environment === obj?.Environment) {
          setCustomProps(obj);
        }
      }).catch(function (error) {
        console.log("Error: " + error);
        if (error instanceof OfficeExtension.Error) {
          console.log("Debug info: " + JSON.stringify(error.debugInfo));
        }
      });
    };

    if (isOfficeInitialized) setProps();
  }, [isOfficeInitialized]);

  useEffect(() => {
    if (messageBar.isOpen) {
      setTimeout(() => {
        setMessageBar(defaultMessageBar);
      }, 10000);
    }
  }, [messageBar]);

  const handleTimerPause = () => {
    setCurrentTab(MAIN_TAB);
    return setSelectedKey(0);
  };

  if (!isOfficeInitialized) {
    return (
      <Progress
        title="WorkNest AddIn"
        logo={require("./../../../assets/WorkNestLogo.svg")}
        message="Please sideload your addin to see app body."
      />
    );
  }
  return (
    <div className={root}>
      <Header
        logo={require("./../../../assets/CaseNest_Purple_Negative_RGB.svg")}
        caseNum={customProps?.CaseId}
        caseDesc={caseSummary?.data?.description}
      />
      {messageBar.isOpen && <MessageBar messageBarType={messageBar.type}>{messageBar.message}</MessageBar>}
      <div className={main}>
        <Pivot
          aria-label="Tabs"
          linkFormat="tabs"
          styles={hasOpenedFromCaseNest ? pivotStyles : snippetOnlyPivotStyles}
          onLinkClick={(e) => {
            setSelectedKey(e.props.itemKey);
            return setCurrentTab(e.props.headerText);
          }}
          selectedKey={String(selectedKey)}
        >
          {hasOpenedFromCaseNest && (
            <PivotItem headerText="Timer" itemKey="0">
              <MainTab />
            </PivotItem>
          )}
          <PivotItem headerText="Snippets" itemKey="1">
            <SnippetsTabComponent
              caseId={customProps?.CaseId}
              caseTypeId={caseSummary?.data?.caseTypeId}
              accountId={caseSummary?.data?.accountId}
              accountName={caseSummary?.data?.accountName}
              onDisplayMessageBar={setMessageBar}
            />
          </PivotItem>
        </Pivot>
        <TimerHOC
          liveCaseDocumentId={customProps?.LiveCaseDocumentId}
          displayTimer={currentTab === MAIN_TAB && hasOpenedFromCaseNest}
          setMessageBar={setMessageBar}
          caseId={customProps?.CaseId}
          onTimerPause={handleTimerPause}
        />
      </div>
    </div>
  );
};
export default App;
